/* global document, window */

import * as Sentry from "@sentry/react";

import { getEnvironment } from "@/lib/get-environment";
import { isInternalEmail } from "@/lib/internal-email";

// Microsoft Clarity 프로젝트 ID (Clarity 대시보드 → Settings → Overview 에서 확인).
const CLARITY_PROJECT_ID = "xdgwsr9vtp";

// 로드 실패 판정 대기 시간. 기업망 프록시는 요청을 즉시 거절하지 않고
// hang 시키는 경우가 있어 onerror 만으로는 잡히지 않는다.
const LOAD_TIMEOUT_MS = 15_000;

type ClarityQueue = {
  (...args: unknown[]): void;
  q?: unknown[][];
};

type ClarityWindow = typeof window & { clarity?: ClarityQueue };

let scriptStatus: "loading" | "loaded" | "failed" = "loading";

/**
 * Clarity 로드 실패를 Sentry 에 보고한다. 세션 누락이 기업망 차단·광고차단기
 * 때문인지 정량 확인하기 위한 계측 — Sentry 유저와 대조해 누가 차단되는지 파악한다.
 */
const reportClarityLoadFailure = (reason: "error" | "timeout") => {
  if (scriptStatus === "failed") return; // 중복 보고 방지
  scriptStatus = "failed";
  Sentry.captureMessage(`Clarity 스크립트 로드 실패 (${reason})`, {
    level: "warning",
    tags: { clarity: "blocked", clarityFailReason: reason },
  });
};

/**
 * Microsoft Clarity 추적 스크립트를 1회 주입한다.
 * SPA 이므로 진입점에서 한 번만 호출하면 이후 모든 라우트 이동이 자동 추적된다.
 * production(app.invector.co) 에서만 동작 — dev/staging/local 트래픽 오염 방지.
 */
export const initClarity = () => {
  if (getEnvironment() !== "production") return;

  const w = window as ClarityWindow;
  const clarity: ClarityQueue =
    w.clarity ||
    function (...args: unknown[]) {
      (clarity.q = clarity.q || []).push(args);
    };
  w.clarity = clarity;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://www.clarity.ms/tag/" + CLARITY_PROJECT_ID;
  script.onload = () => {
    if (scriptStatus === "loading") scriptStatus = "loaded";
  };
  script.onerror = () => reportClarityLoadFailure("error");
  window.setTimeout(() => {
    if (scriptStatus === "loading") reportClarityLoadFailure("timeout");
  }, LOAD_TIMEOUT_MS);

  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
};

/**
 * Clarity 세션을 실제 앱 유저와 연결한다 (Identify API).
 * 대시보드의 방문자 정보에 무작위 Clarity ID 대신 friendlyName(= 이메일)이 표시되고,
 * 같은 유저의 세션이 기기·브라우저가 달라도 하나로 묶인다.
 *
 * Clarity 세션은 30분 무활동 시 만료 후 새로 시작되며 커스텀 태그는 세션 단위라,
 * 로그인 시 1회 호출로는 부족하다 — 라우트 변경·주기 재호출로 매 세션에 태그를 보장할 것
 * (useClarityIdentify 훅 참조). Microsoft 도 매 페이지 방문마다 호출을 권장한다.
 *
 * initClarity() 가 실행되지 않은 환경(dev/staging/local)에서는 no-op.
 */
export const identifyClarityUser = (userId: string, email?: string | null) => {
  const w = window as ClarityWindow;
  if (!w.clarity) return;

  try {
    // 인자: custom-id(해시되어 저장), custom-session-id, custom-page-id, friendly-name(원문 표시)
    w.clarity("identify", userId, undefined, undefined, email ?? userId);
    // 필터·검색용 커스텀 태그로도 저장
    w.clarity("set", "userId", userId);
    if (email) w.clarity("set", "email", email);
    // 대시보드에서 "internal = yes 제외" 하나로 사내 인원을 걸러 보기 위한 태그.
    // email 태그는 패턴 검색이 안 돼 계정을 하나씩 골라 빼야 한다.
    w.clarity("set", "internal", isInternalEmail(email) ? "yes" : "no");
  } catch {
    // 분석 실패가 인증 플로우로 전파되지 않도록 격리
  }
};
